import { Emitter, type Event } from '@patsnap/tahiti-core';
import type { OarExtractBasicInfo } from '@/types/project-event';

export interface OarListEventItem {
    claim?: string;
    statute?: string;
    claim_element?: string;
    examiner_reasoning?: string;
    prior_art?: string;
    distinguishing_features?: string;
    analysis_direction?: string;
    analysis_strategy?: string;
    [key: string]: unknown;
}

interface OarSessionEvent {
    eventId: string;
    conversationId: string;
    rejectionDataId?: string;
}

export type OarBasicStartEvent = OarSessionEvent;
export interface OarBasicStreamEvent extends OarSessionEvent {
    index: number;
    item: OarExtractBasicInfo;
    isComplete: false;
}
export interface OarBasicCompleteEvent extends OarSessionEvent {
    data: OarExtractBasicInfo[];
}
export type OarListStartEvent = OarSessionEvent;
export interface OarListStreamEvent extends OarSessionEvent {
    index: number;
    item: OarListEventItem;
    isComplete: false;
}
export interface OarListCompleteEvent extends OarSessionEvent {
    data: OarListEventItem[];
    basicInfo?: unknown[];
}

/** OAR 流式会话的只读订阅边界。 */
export interface OarEventSource {
    readonly onBasicStart: Event<OarBasicStartEvent>;
    readonly onBasicStream: Event<OarBasicStreamEvent>;
    readonly onBasicComplete: Event<OarBasicCompleteEvent>;
    readonly onListStart: Event<OarListStartEvent>;
    readonly onListStream: Event<OarListStreamEvent>;
    readonly onListComplete: Event<OarListCompleteEvent>;
}

/** OAR 流式会话的发布边界。 */
export interface OarEventPublisher {
    publishBasicStart(event: OarBasicStartEvent): void;
    publishBasicStream(event: OarBasicStreamEvent): void;
    publishBasicComplete(event: OarBasicCompleteEvent): void;
    publishListStart(event: OarListStartEvent): void;
    publishListStream(event: OarListStreamEvent): void;
    publishListComplete(event: OarListCompleteEvent): void;
}

/** 创建应用级、可隔离测试的 OAR 事件通道。 */
export function createOarEvents(): OarEventSource & OarEventPublisher {
    const basicStart = new Emitter<OarBasicStartEvent>();
    const basicStream = new Emitter<OarBasicStreamEvent>();
    const basicComplete = new Emitter<OarBasicCompleteEvent>();
    const listStart = new Emitter<OarListStartEvent>();
    const listStream = new Emitter<OarListStreamEvent>();
    const listComplete = new Emitter<OarListCompleteEvent>();
    return {
        onBasicStart: basicStart.event,
        onBasicStream: basicStream.event,
        onBasicComplete: basicComplete.event,
        onListStart: listStart.event,
        onListStream: listStream.event,
        onListComplete: listComplete.event,
        publishBasicStart: event => basicStart.fire(event),
        publishBasicStream: event => basicStream.fire(event),
        publishBasicComplete: event => basicComplete.fire(event),
        publishListStart: event => listStart.fire(event),
        publishListStream: event => listStream.fire(event),
        publishListComplete: event => listComplete.fire(event),
    };
}

export const oarEvents = createOarEvents();
